import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ShieldCheck, 
  ShieldX, 
  Clock, 
  User, 
  Building, 
  CheckCircle2, 
  XCircle,
  Search,
  Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyRecord = () => {
  const { hash } = useParams();
  const [searchParams] = useSearchParams();
  const [hashInput, setHashInput] = useState(hash || searchParams.get('hash') || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [appName, setAppName] = useState('Regulisto');

  useEffect(() => {
    fetchAppName();
  }, []);

  const fetchAppName = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/public`);
      if (response.data?.app_name) {
        setAppName(response.data.app_name);
      }
    } catch (error) {
      console.error('Error fetching public settings:', error);
    }
  };

  useEffect(() => {
    if (hash) {
      verifyHash(hash);
    }
  }, [hash]);

  const verifyHash = async (code) => {
    if (!code || code.length < 8) {
      setError('El código debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.get(`${API_URL}/api/verify/${code.toUpperCase()}`);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al verificar el código');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    verifyHash(hashInput);
  };

  const typeColors = {
    'Entrada': 'bg-green-100 text-green-800',
    'Salida': 'bg-red-100 text-red-800',
    'Inicio descanso': 'bg-amber-100 text-amber-800',
    'Fin descanso': 'bg-blue-100 text-blue-800',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 mb-4">
            <ShieldCheck className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Verificación de Fichajes
          </h1>
          <p className="text-slate-600">
            Verifica la autenticidad de un registro de control horario
          </p>
        </div>

        {/* Search Form */}
        <Card className="border-slate-200 mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="flex gap-3">
              <Input
                value={hashInput}
                onChange={(e) => setHashInput(e.target.value.toUpperCase())}
                placeholder="Introduce el código de verificación (ej: A1B2C3D4)"
                className="font-mono text-lg"
                maxLength={16}
                data-testid="verify-hash-input"
              />
              <Button 
                type="submit" 
                disabled={loading || hashInput.length < 8}
                className="bg-teal-600 hover:bg-teal-700 px-6"
                data-testid="verify-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Verificar
                  </>
                )}
              </Button>
            </form>
            <p className="text-xs text-slate-500 mt-2">
              El código de verificación se encuentra en los informes PDF exportados desde {appName}
            </p>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-red-700">
                <XCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Result */}
        {result && (
          <Card className={result.verified ? 'border-green-200' : 'border-red-200'}>
            <CardHeader className={result.verified ? 'bg-green-50' : 'bg-red-50'}>
              <CardTitle className="flex items-center gap-3">
                {result.verified ? (
                  <>
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <span className="text-green-800">Fichaje Verificado</span>
                  </>
                ) : (
                  <>
                    <ShieldX className="w-6 h-6 text-red-600" />
                    <span className="text-red-800">No Verificado</span>
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-slate-600 mb-4">{result.message}</p>
              
              {result.verified && result.records && result.records.length > 0 && (
                <div className="space-y-4">
                  {result.records.map((record, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="text-xs text-slate-500">Empleado</p>
                            <p className="font-medium text-slate-900">{record.employee_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="text-xs text-slate-500">Organización</p>
                            <p className="font-medium text-slate-900">{record.organization}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="text-xs text-slate-500">Fecha y hora</p>
                            <p className="font-medium text-slate-900">{record.timestamp}</p>
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Tipo de fichaje</p>
                          <Badge className={typeColors[record.record_type] || 'bg-slate-100 text-slate-800'}>
                            {record.record_type}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="flex flex-wrap gap-3">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-600">
                              {record.is_locked ? 'Registro bloqueado (inmutable)' : 'Registro modificable'}
                            </span>
                          </div>
                          
                          {record.is_correction && (
                            <Badge className="bg-amber-100 text-amber-800">
                              Corrección de incidencia
                            </Badge>
                          )}
                          
                          {record.integrity_verified && (
                            <Badge className="bg-green-100 text-green-800">
                              Integridad verificada
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-3 p-2 bg-slate-100 rounded">
                        <p className="text-xs text-slate-500">Hash de verificación</p>
                        <p className="font-mono text-sm text-slate-700">{record.record_hash}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {!result.verified && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    El código <span className="font-mono font-medium">{result.hash}</span> no corresponde a ningún fichaje registrado en el sistema.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Section */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Este servicio permite verificar la autenticidad de los registros de control horario 
            generados por {appName}.
          </p>
          <p className="mt-2">
            Los códigos de verificación se incluyen en los informes PDF exportados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyRecord;
